import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';
import { Card, Button } from '../ui';

export default function UrlDisplay({
  title,
  subtitle,
  url,
  description,
  className,
}) {
  const getDomain = (urlString) => {
    try {
      const urlObj = new URL(urlString);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return null;
    }
  };

  const { t } = useTranslation();
  const domain = url ? getDomain(url) : null;

  return (
    <Card
      gradient
      elevated
      className={cn('overflow-hidden', className)}
    >
      <div className="py-8 px-6 text-center">
        {/* Icon */}
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-gray-600 dark:text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {title}
        </h1>
        {subtitle && (
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">{subtitle}</p>
        )}

        {/* Domain indicator */}
        {domain && (
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">
            {t('stimulus.contentFrom', { domain })}
          </p>
        )}

        {/* Description */}
        {description && (
          <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-lg mx-auto">
            {description}
          </p>
        )}

        {/* Open link button */}
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'inline-flex items-center gap-2 px-6 py-3',
              'bg-gradient-to-b from-gray-800 to-black text-white',
              'dark:from-white dark:to-gray-200 dark:text-gray-900',
              'rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.2)]',
              'hover:shadow-[0_4px_12px_rgba(0,0,0,0.3)]',
              'hover:from-gray-700 hover:to-gray-900',
              'dark:hover:from-gray-100 dark:hover:to-gray-300',
              'transition-all duration-200'
            )}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
            {t('stimulus.openExternal')}
          </a>
        )}

        {/* Instructions */}
        <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-900 dark:text-white">{t('stimulus.instructions')}</span>
            {t('stimulus.externalHelp')}
          </p>
        </div>
      </div>
    </Card>
  );
}
